'use es6';

import { getTreatmentsFromHttp, getTreatmentsFromQuickFetch } from './api';
import { filterObject, createQueryObject, ensureFn } from './helpers';
import { verifyIfNetworkRequestIsNeeded } from './utils';
import { getOverridesFromSearch, getOverridesFromCache } from './overrides';
import { DEBUG_MESSAGES } from '../messages';

const mapSchemaToNetworkRequest = schema => {
  const entries = Object.entries(schema);
  return entries.map(([key, {
    identifier
  }]) => ({
    key,
    identifier
  }));
};

export function getAsyncNetworkAdapter({
  apiDomain,
  ceid,
  http,
  quickFetch,
  quickFetchLabel,
  schema,
  identifiers,
  timeout
} = {}) {
  const getCommonParams = () => ({
    apiDomain,
    timeout,
    // this removes all the properties that are empty or undefined
    query: filterObject(Object.assign({}, identifiers, {
      ceid
    })),
    data: mapSchemaToNetworkRequest(schema)
  });

  const getDataFromHttp = (opts = {}) => {
    return getTreatmentsFromHttp(Object.assign({}, getCommonParams(), {
      onComplete: opts.onComplete,
      onError: opts.onError,
      http
    }));
  };

  const getDataFromQuickFetch = (opts = {}) => {
    return getTreatmentsFromQuickFetch(Object.assign({}, getCommonParams(), {
      onComplete: opts.onComplete,
      onError: opts.onError,
      quickFetch,
      quickFetchLabel
    }));
  };

  if (quickFetchLabel) {
    return getDataFromQuickFetch;
  }

  return getDataFromHttp;
}
export function wrapNetworkAdapterWithMeta({
  networkAdapter,
  asyncStore,
  cacheKeys,
  ceid,
  search,
  schema,
  onError,
  logDebug,
  useCacheOnly
}) {
  const searchParamsObject = createQueryObject(search);
  const safeLogDebug = ensureFn(logDebug); // The name of the method to be used within the debug logs

  const methodName = 'wrapNetworkAdapterWithMeta'; // This override disables `laboratory-lib` from fetching requests.
  // And it will fallback to the treatments or experiments schema

  const preventRequests = 'laboratorySkipTreatmentRequest' in searchParamsObject || useCacheOnly;
  const overridesFromSearch = getOverridesFromSearch({
    cacheKeys,
    searchParamsObject
  });
  const asyncOverridesFromCache = getOverridesFromCache({
    cacheKeys,
    asyncStore,
    onError
  });
  const data = {
    overridesFromCache: {},
    overridesFromSearch,
    schema,
    treatments: []
  };
  return (opts = {}) => {
    return asyncOverridesFromCache.then(overridesFromCache => {
      data.overridesFromCache = overridesFromCache;
      safeLogDebug(DEBUG_MESSAGES.resolvedFromCache, methodName);
      const requiresNetwork = verifyIfNetworkRequestIsNeeded({
        ceid,
        schema,
        cacheKeys,
        overridesFromCache
      });
      safeLogDebug(!preventRequests && requiresNetwork ? DEBUG_MESSAGES.networkRequestsNeeded : DEBUG_MESSAGES.noNetworkRequestsNeeded, methodName);
      const safeOnComplete = ensureFn(opts.onComplete);

      if (!preventRequests && requiresNetwork) {
        networkAdapter({
          onComplete: (treatments = []) => {
            safeLogDebug(DEBUG_MESSAGES.resolvedFromNetwork, methodName);
            data.treatments = treatments;
            safeOnComplete(data);
          },
          onError: err => {
            safeLogDebug(DEBUG_MESSAGES.failedResolveFromNetwork, methodName);
            ensureFn(opts.onError)(err, data);
          }
        });
      } else {
        safeOnComplete(data);
      }
    });
  };
}